import useList, { ListActions } from 'react-use/lib/useList';
import {
  OrionModelOptionDTO,
  useOrionModelOptionsQuery,
} from '../../api/useOrionModelOptionsQuery';
import { computeRiskBands } from '../../utils/computeRiskBands';
import { ExternalSystemId, ModelDTO, ModelGroupDTO } from '../../api/useModelGroupsQuery';
import { ComponentHookResult } from '../../../../../Types/ComponentHookResult';
import { makeNewModel } from '../../utils/makeNewModel';

interface Data {
  models: ModelDTO[];
  modelsOptions: OrionModelOptionDTO[] | null;
}

export interface UpsertModelGroupActions {
  addNewModel(): void;
  setModels: ListActions<ModelDTO>['set'];
  removeModelAt(index: number): void;
  moveModel(fromIndex: number, toIndex: number): void;
  updateModelAt(index: number, model: ModelDTO): void;
  validateRiskRangeTo(value: number | string, index: number): void;
}

interface ReturnType extends ComponentHookResult<Data, UpsertModelGroupActions> {
  isLoading: boolean;
  error: Error | null;
  refetch(): void;
}

interface Params {
  modelGroup: Partial<ModelGroupDTO>;
}

export const useUpsertModelGroup = ({ modelGroup }: Params): ReturnType => {
  const queryResult = useOrionModelOptionsQuery({ modelGroup });

  const [models, modelsActions] = useList<ModelDTO>(
    modelGroup.modelPortfolios ||
      (modelGroup.externalSystem === ExternalSystemId.Orion ? [makeNewModel()] : []),
  );

  const addNewModel = () => {
    const riskBands = computeRiskBands(models.length + 1);

    const newModels = models.map((model, index) => ({
      ...model,
      riskRange: {
        from: riskBands[index][0],
        to: riskBands[index][1],
      },
    }));

    modelsActions.set([
      ...newModels,
      makeNewModel({
        riskRangeFrom: riskBands[models.length][0],
        riskRangeTo: riskBands[models.length][1],
      }),
    ]);
  };

  const removeModelAt = (index: number) => {
    const riskBands = computeRiskBands(models.length - 1);

    const newModels = models.slice();
    newModels.splice(index, 1);

    modelsActions.set(
      newModels.map((model, i) => ({
        ...model,
        riskRange: {
          from: riskBands[i][0],
          to: riskBands[i][1],
        },
      })),
    );
  };

  const moveModel = (fromIndex: number, toIndex: number) => {
    const riskBands = computeRiskBands(models.length);

    const modelToMove = models[fromIndex];
    const newModels = models.slice();
    newModels.splice(fromIndex, 1);
    newModels.splice(toIndex, 0, modelToMove);

    modelsActions.set(
      newModels.map((model, i) => ({
        ...model,
        riskRange: {
          from: riskBands[i][0],
          to: riskBands[i][1],
        },
      })),
    );
  };

  const validateRiskRangeTo = (value: number | string, index: number) => {
    const parsedValue = Number(value);

    const min = index === 0 ? 2 : Number(models[index].riskRange.from) + 1;
    const max = index === models.length - 2 ? 98 : models[index + 1].riskRange.to - 1;

    const valueToRestore = Number(models[index + 1].riskRange.from) - 1;

    // If we fail the validation, restore the value
    if (Number.isNaN(parsedValue) || parsedValue < min || parsedValue > max) {
      modelsActions.updateAt(index, {
        ...models[index],
        riskRange: {
          ...models[index].riskRange,
          to: index - 1 === models.length ? 100 : valueToRestore,
        },
      });
    } else {
      modelsActions.updateAt(index + 1, {
        ...models[index + 1],
        riskRange: {
          ...models[index + 1].riskRange,
          from: Math.round(parsedValue) + 1,
        },
      });

      // Keep the value as an integer
      modelsActions.updateAt(index, {
        ...models[index],
        riskRange: {
          ...models[index].riskRange,
          to: Math.round(parsedValue),
        },
      });
    }
  };

  return {
    ...queryResult,
    data: {
      models,
      modelsOptions: queryResult.data || null,
    },

    actions: {
      addNewModel,
      moveModel,
      removeModelAt,
      setModels: modelsActions.set,
      updateModelAt: modelsActions.updateAt,
      validateRiskRangeTo,
    },
  };
};
